import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ComplianceReportGenerator = ({ onGenerateReport }) => {
  const [reportConfig, setReportConfig] = useState({
    reportType: 'comprehensive',
    timeRange: 'last_quarter',
    includeCredits: true,
    includeAuthorizations: true,
    includeConflicts: true,
    includeRecommendations: true,
    format: 'pdf',
    hostCountries: [],
    customDateRange: {
      start: '',
      end: ''
    }
  });

  const reportTypeOptions = [
    { value: 'comprehensive', label: 'Comprehensive Compliance Report' },
    { value: 'authorization_summary', label: 'Authorization Status Summary' },
    { value: 'conflict_analysis', label: 'Double-Counting Analysis' },
    { value: 'ndc_alignment', label: 'NDC Alignment Assessment' },
    { value: 'ca_tracking', label: 'Corresponding Adjustment Tracking' }
  ];

  const timeRangeOptions = [
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data Export' },
    { value: 'json', label: 'JSON Data Export' }
  ];

  const countryOptions = [
    { value: 'brazil', label: 'Brazil' },
    { value: 'colombia', label: 'Colombia' },
    { value: 'peru', label: 'Peru' },
    { value: 'chile', label: 'Chile' },
    { value: 'costa_rica', label: 'Costa Rica' },
    { value: 'mexico', label: 'Mexico' }
  ];

  const mockReportTemplates = [
    {
      id: 'template-001',
      name: 'UNFCCC Article 6 Compliance',
      description: 'Standard report for UNFCCC Article 6.2 and 6.4 compliance',
      lastUsed: '2025-01-15T10:30:00Z',
      popularity: 95
    },
    {
      id: 'template-002',
      name: 'Corporate Inventory Verification',
      description: 'Verification report for corporate carbon inventory claims',
      lastUsed: '2025-01-18T14:20:00Z',
      popularity: 87
    },
    {
      id: 'template-003',
      name: 'Registry Compliance Audit',
      description: 'Multi-registry compliance and authorization audit',
      lastUsed: '2025-01-20T09:45:00Z',
      popularity: 78
    }
  ];

  const handleConfigChange = (key, value) => {
    setReportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCustomDateChange = (field, value) => {
    setReportConfig(prev => ({
      ...prev,
      customDateRange: {
        ...prev?.customDateRange,
        [field]: value
      }
    }));
  };

  const handleGenerateReport = () => {
    onGenerateReport(reportConfig);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">Compliance Report Generator</h2>
            <p className="text-sm text-muted-foreground">Generate comprehensive Article 6 compliance documentation</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Report Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-card-foreground">Report Configuration</h3>
          
          <Select
            label="Report Type"
            options={reportTypeOptions}
            value={reportConfig?.reportType}
            onChange={(value) => handleConfigChange('reportType', value)}
          />

          <Select
            label="Time Range"
            options={timeRangeOptions}
            value={reportConfig?.timeRange}
            onChange={(value) => handleConfigChange('timeRange', value)}
          />

          {reportConfig?.timeRange === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={reportConfig?.customDateRange?.start}
                onChange={(e) => handleCustomDateChange('start', e?.target?.value)}
              />
              <Input
                label="End Date"
                type="date"
                value={reportConfig?.customDateRange?.end}
                onChange={(e) => handleCustomDateChange('end', e?.target?.value)}
              />
            </div>
          )}

          <Select
            label="Host Countries (Optional)"
            options={countryOptions}
            value={reportConfig?.hostCountries}
            onChange={(value) => handleConfigChange('hostCountries', value)}
            multiple
            searchable
            placeholder="All countries"
          />

          <Select
            label="Export Format"
            options={formatOptions}
            value={reportConfig?.format}
            onChange={(value) => handleConfigChange('format', value)}
          />

          {/* Report Sections */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-card-foreground">Include Sections</h4>
            <div className="space-y-2">
              <Checkbox
                label="Credit Portfolio Summary"
                checked={reportConfig?.includeCredits}
                onChange={(e) => handleConfigChange('includeCredits', e?.target?.checked)}
              />
              <Checkbox
                label="Authorization Status Details"
                checked={reportConfig?.includeAuthorizations}
                onChange={(e) => handleConfigChange('includeAuthorizations', e?.target?.checked)}
              />
              <Checkbox
                label="Double-Counting Analysis"
                checked={reportConfig?.includeConflicts}
                onChange={(e) => handleConfigChange('includeConflicts', e?.target?.checked)}
              />
              <Checkbox
                label="Compliance Recommendations"
                checked={reportConfig?.includeRecommendations}
                onChange={(e) => handleConfigChange('includeRecommendations', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Report Templates */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-card-foreground">Quick Templates</h3>
          
          <div className="space-y-3">
            {mockReportTemplates?.map((template) => (
              <div key={template?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors cursor-pointer">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-medium text-card-foreground">{template?.name}</h4>
                    <p className="text-xs text-muted-foreground">{template?.description}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="TrendingUp" size={12} className="text-success" />
                    <span className="text-xs text-success">{template?.popularity}%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Last used: {formatDate(template?.lastUsed)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    onClick={() => onGenerateReport({ ...reportConfig, template: template?.id })}
                  >
                    Use Template
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Report Preview */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-card-foreground mb-3">Report Preview</h4>
            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Estimated pages:</span>
                <span className="font-medium text-card-foreground">12-15</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Generation time:</span>
                <span className="font-medium text-card-foreground">~2 minutes</span>
              </div>
              <div className="flex items-center justify-between">
                <span>File size:</span>
                <span className="font-medium text-card-foreground">~3.2 MB</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Credits included:</span>
                <span className="font-medium text-card-foreground">3,392</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Generation Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>Reports are generated in real-time with latest data</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            iconName="Eye"
            onClick={() => onGenerateReport({ ...reportConfig, preview: true })}
          >
            Preview Report
          </Button>
          <Button
            variant="default"
            iconName="Download"
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
        </div>
      </div>
      {/* Recent Reports */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-medium text-card-foreground mb-3">Recent Reports</h4>
        <div className="space-y-2">
          {[
            { name: 'Q4 2024 Compliance Report', date: '2025-01-20', size: '2.8 MB', format: 'PDF' },
            { name: 'Authorization Status Summary', date: '2025-01-18', size: '1.2 MB', format: 'Excel' },
            { name: 'Double-Counting Analysis', date: '2025-01-15', size: '856 KB', format: 'PDF' }
          ]?.map((report, index) => (
            <div key={index} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="FileText" size={16} className="text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium text-card-foreground">{report?.name}</div>
                  <div className="text-xs text-muted-foreground">{report?.date} • {report?.size} • {report?.format}</div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button variant="ghost" size="sm" iconName="Download" />
                <Button variant="ghost" size="sm" iconName="Share" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReportGenerator;